/**
 * English copy dictionary.
 *
 * Every string is verbatim from docs/content/website_content_pass_v1.md,
 * with facts replaced by {placeholders}. Do not add numbers, prices,
 * addresses, phone numbers or barber data here — see src/content/types.ts
 * and docs/COPY_STYLE.md.
 */

import type { CopyDict } from './types';

export const en = {
  tagline: {
    header: '{city}, {stateFull} · Since {foundedYear}',
    footer: '{city}, {state} · Since {foundedYear}',
  },
  localeToggle: { label: 'Language', en: 'EN', es: 'ES' },

  nav: {
    book: 'Book',
    barbers: 'Barbers',
    gallery: 'Gallery',
    walkIns: 'Walk-ins',
    visit: 'Visit',
    home: 'Home',
    about: 'About',
    menu: 'Menu',
    services: 'Services',
    openMenu: 'Menu',
    closeMenu: 'Close menu',
  },

  meta: {
    localeName: 'English',
    days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Saturday', sun: 'Sunday' },
    weekdayRange: 'Mon – Fri',
    closed: 'Closed',
    hoursLabel: 'Hours',
  },

  bookRedirect: {
    headline: 'Taking you to Booksy…',
    body: "If nothing happens in a second, use the button below — booking for {shopName} lives on Booksy.",
    cta: 'Book on Booksy',
  },

  home: {
    hero: {
      kicker: '{city}, {stateFull} · Since {foundedYear}',
      boricuaPill: 'Boricua-owned in {city} since {foundedYear}',
      headline: "{city}' most-booked barbershop.",
      subhead: {
        lead: 'Come sit down — we saved you a chair. {barberCount} barbers, one crown. Book a time that suits you, or walk in — ',
        accent: 'sin cita, sin problema',
        tail: " — and we'll text you when the chair is warm.",
      },
      intro: {
        line1:
          "Come take a seat — we've got a chair waiting for you. Getting a fresh cut has never been easier.",
        line2: 'Book a time that works for you or just walk in. No appointment, no problem',
      },
      est: 'Est. {foundedYear}',
      ctaPrimary: 'Book your chair',
      ctaSecondary: 'Join the walk-in line',
      appointmentDeskLabel: 'The appointment desk',
      openToday: 'Open today',
      closedToday: 'Closed today',
      desk: {
        line1: 'A sharper plan',
        line2Prefix: 'for',
        cta: 'Choose a time',
        today: 'today',
        tomorrow: 'tomorrow',
        closedBooksyCta: 'Or book on Booksy now',
      },
      mobileCard: { label: 'The chair' },
      liveStatus: {
        open: 'About {n} minutes right now',
        meta: '{cutting} chairs cutting, {waiting} folks waiting',
        empty: 'Chair open right now',
        closed: 'Opens {when}',
        whenToday: 'today at {time}',
        whenTomorrow: 'tomorrow at {time}',
        whenDay: '{day} at {time}',
        staffed: 'Ready when you are · {count} at the chair',
        staffedOne: 'Ready when you are · one chair cutting',
        unstaffed: 'Chairs open when the doors do · today at {openTime}',
        closedUntil: 'See you {when} · we open at {openTime}',
        whenTomorrowBare: 'tomorrow',
        whenDayBare: '{day}',
      },
    },
    shopStory: {
      eyebrow: 'The shop · La tienda',
      headline: 'Busy shop. Serious skill.',
      body:
        'Kenny came to {city} with a pair of clippers and a plan. {yearsSinceFounding} years later there\u2019s a chair with his name on it, {barbersBesidesOwner} barbers alongside him, and {combinedReviewTotal} people who keep coming back. Come see why.',
      meta: 'Family-owned. Boricua-owned. Fully booked most Saturdays.',
      imageAlt: 'Kenny cutting a young client at {shopName}',
      mission: {
        heading: 'Our Mission',
        lead: 'At ',
        emph: '{shopName}',
        tail: ', our mission is to provide high-quality grooming services in a welcoming, professional, and comfortable environment. We are committed to making every client feel confident and refreshed through great service, attention to detail, and a personalized experience.',
      },
      vision: {
        heading: 'Our Vision',
        p1: 'Our vision is to be a trusted barbershop in the ',
        emph1: '{city} community',
        p2: ', known for quality, consistency, and exceptional customer service. We strive to create more than just great haircuts—we want ',
        emph2: '{shopName}',
        p3: ' to be a place where every client feels welcome, valued, and confident every time they walk through our doors.',
      },
    },
    liveStrip: {
      quiet: "The shop is quiet. Good time to book.",
      booked: '{name} just booked with {barber} — {time}',
      checkedIn: '{name} just checked in for a walk-in',
    },
    whyKd: {
      kicker: 'Why KD · Por qué KD',
      headline: 'Booked shop. Big skill. Zero attitude.',
      columnLabels: {
        booking: 'Booked or walk-in',
        quality: 'Cuts that hold up',
        location: 'Right on {streetName}',
      },
      columns: {
        booking:
          'Book the exact time you want, or come in and grab the next open chair. Either works.',
        quality:
          "Every barber on the team is on Booksy at {minBarberRating}+ stars. We don't hire barbers who don't have the reviews to prove it.",
        location: "Corner of {crossStreet}. Free parking. Wifi's {wifiPassword} if you're waiting.",
      },
    },
    works: {
      eyebrow: 'Signature ritual',
      headlineLine1: 'The Works.',
      headlineLine2: 'No corners cut.',
      ritualBody:
        'The full hour. Haircut, beard shaping, brows, nose wax, and a hot-towel finish. The service people tell their friends about.',
      priceLabel: 'One hour in the chair',
      ctaLabel: 'Book The Works',
      imageCaption: 'The finish matters',
      kicker: 'The signature service · Servicio estrella',
      headline: 'The Works — {worksPrice}',
      body:
        "Haircut. Beard line-up. Eyebrows cleaned up. Nose wax. Hot towel finish. One hour in the chair, and you walk out looking like you're getting married that afternoon. This is what we're known for.",
      cta: 'Book The Works →',
    },
    crew: {
      kicker: 'Meet the Barbers · Conoce a los Barberos',
      bookAny: 'Book any chair →',
      book: 'Book →',
      bookWith: 'Book with {name} →',
      tenure: '{years} years',
      photoAlt: '{name}, {role} at {shopName}',
      ownerPhotoAlt: '{name}, owner, cutting a client at {shopName}',
      nextAt: 'Next at {time}',
      bookNow: 'Book now →',
      headline: 'Pick your chair.',
      noPreference: "No preference? We'll match you with the next open chair.",
      cta: 'Book online →',
      lines: {
        orlando: {
          short:
            "The most-booked chair in the shop. Fades so clean you'll want to keep the shape for a month.",
          bio:
            '{orlandoYears}+ years of experience. Specializes in fades, beard work, and precision line-ups. The most-booked chair in the shop for a reason.',
        },
        kenny: {
          short: 'Owner. {kennyYears} years behind the chair. Ask him anything.',
          bio:
            "Owner, {kennyYears}+ years behind the chair. Bilingual (English/Spanish). If you're not sure who to book with, ask for Kenny.",
        },
        rafy: {
          short: '{rafyYears} years of precision. Old-school skills, modern eye.',
          bio:
            '{rafyYears} years of barbering experience. Old-school skills, modern eye. Regulars swear by his fades and his straight-razor beard work.',
        },
        dylan: {
          short: 'Young talent. Fast, sharp, and completely dialed in on modern styles.',
          bio:
            '{dylanYears}+ years in the game and one of the fastest-rising chairs in the shop. Great for modern cuts, sharp designs, and clean line-ups.',
        },
        emanuel: {
          short: 'Newest to the team. Already turning first-time clients into regulars.',
          bio:
            'Newest to the team, but not new to the craft. Building his book fast — and turning first-time clients into weekly regulars.',
        },
      },
    },
    gallery: {
      eyebrow: 'The work · El trabajo',
      headline: 'Fresh off the chair.',
      meta: 'Bring a photo or leave it to us — either way you walk out sharp.',
      instagramCta: {
        full: 'See more on Instagram — @{instagramHandle}',
        short: 'Instagram — @{instagramHandle}',
      },
      eyebrowSimple: 'Gallery · Galería',
      labels: {
        'skin-fade-side-profile': 'Skin fade — side profile',
        'fade-natural-top': 'Fade — natural top',
        'kids-fade-curly-top': 'Kids fade — curly top',
        'lineup-detail': 'Line-up — clean edge',
      },
    },
    walkins: {
      eyebrow: 'Walk-ins · Sin cita',
      headline: 'No appointment? Still welcome.',
      body: "There's a tablet by the door. Put your name down, then go grab a coffee — we'll text you when your chair is close.",
      steps: [
        'Tap your name into the tablet by the door.',
        'Choose your barber, or take whoever opens up first.',
        "We text you when you're up — walk back in and sit down.",
      ],
      ctaPrimary: 'Get in the walk-in line',
      ctaSecondary: 'Call now · {shopPhone}',
      capeCaption: "Kenny's chair. Your name next.",
      imageAlt: 'A {shopName} cape laid over a barber chair, waiting',
    },
    visit: {
      eyebrow: 'Visit · Visítanos',
      headline: '{addressLine1}, {city}',
      body: "Right on the corner of {crossStreet}. Plenty of parking out front — you'll hear the shop before you see it.",
      hoursCard: {
        weekdays: 'Monday to Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
        sundayClosed: 'Closed — see you Monday',
      },
      ctaPrimary: 'Call the shop',
      ctaSecondary: 'Get directions',
      callUs: 'Call us',
      pinTitle: '{shopName} · {streetName}',
      pinSubtitle: 'On State Road 54 · {plazaName} · parking out front',
      mapAlt: 'Satellite view of {shopName} on {streetName}, {city}',
      mapTitle: '{shopName} location map',
    },
    theSpace: {
      eyebrow: 'The Shop · La Barbería',
      headlineLine1: 'More than five chairs',
      headlineLine2: 'and a good fade.',
      body: "Pool table's open. Dominos are on the counter. Snacks and cold drinks in the machine. Bring the kids, take your time — the fade will still be fresh when you're done hanging out.",
      amenities: ['Pool table', 'Dominos ready', 'Snacks & cold drinks'],
    },
    menu: {
      eyebrow: 'The menu · La carta',
      headline: 'From {seniorFromPrice} senior cuts to {worksPrice} works.',
      subhead: "Everything on the board. Add what you need. Kenny's team will make it fresh.",
      featured: {
        label: 'The signature',
        description:
          "Cut, beard, hot towel, shape-up, eyebrows. One hour in the chair — you'll walk out looking like the version of you that means business.",
      },
      haircutsLabel: 'Haircuts / Recortes',
      addonsLabel: 'Add-ons',
      addonsSubhead: 'Stack anything onto a haircut.',
      softCtaIntro: 'Not sure what you need?',
      softCtaLink: 'Call Kenny',
      worksSubline: 'Cut, beard, hot towel, shape-up, eyebrows.',
      softCtaCall: 'Call us',
    },
    welcome: {
      est: 'Est. {foundedYear}',
      line1:
        "Come take a seat — we've got a chair waiting for you. Getting a fresh cut has never been easier.",
      line2: 'Book a time that works for you or just walk in. No appointment, no problem',
    },
    carousel: { prev: 'Previous', next: 'Next' },
    trustRibbon: {
      eyebrow: 'The house standard',
      items: ['Your barber, your call', 'Easy walk-in line', 'Parking out front'],
      cta: 'Start booking',
    },
    sectionNumbers: {
      shopStory: '01',
      signature: '02',
      crew: '03',
      gallery: '04',
      walkins: '05',
    },
    trustStrip: {
      reviewsNoun: 'reviews',
      averageLabel: 'average',
      source: 'on Booksy + Google',
      starsAlt: '{combinedRating} out of 5 stars',
    },
    servicesTeaser: {
      kicker: 'The menu',
      headline: 'From {seniorFromPrice} senior cuts to {worksPrice} works.',
      haircutsLabel: 'Haircuts',
      addOnsLabel: 'Add-ons',
      cta: 'Full menu, prices, and how long each takes on the services page →',
    },
    reviews: {
      kicker: 'What people say',
      headline: '{combinedReviewTotal} reviews. {combinedRating}-star average.',
      subline: 'Across Booksy and Google. Real customers, real cuts.',
      sublineGoogle: 'Real customers, real cuts on Google.',
      errorFallbackHeadline: 'Read what people say',
      errorFallbackCta: 'See reviews on Google',
      // Real Booksy-verified reviews, captured 2026-08-24. Quotes are verbatim.
      // Replacing any of these requires a real, attributable review — never
      // invented copy (docs/COPY_STYLE.md).
      reviewQuotes: [
        {
          quote: 'Satisfied every time. Fast and efficient.',
          attribution: 'Elton · Aug 2026 · Booksy',
        },
        {
          quote:
            'This is an outstanding Barbershop. There is not one bad Barber in the shop — you will always leave satisfied.',
          attribution: 'George · Aug 2026 · Booksy',
        },
        {
          quote: 'Kenny is the best. He always takes care of me, my mom and my sons.',
          attribution: 'Tymira · Aug 2026 · Booksy',
        },
      ],
      ctaGoogle: 'Read more on Google →',
      ctaBooksy: 'Booksy →',
    },
    location: {
      kicker: 'Come see us',
      directionsCta: 'Get directions →',
      contactLabel: 'Call or text:',
    },
    footerCta: {
      headline: 'Ready when you are.',
      subline: "Book online, walk in, or call. Whatever's easiest.",
      ctaBook: 'Book online',
      ctaCall: 'Call {shopPhone}',
    },
  },

  about: {
    hero: {
      kicker: 'About the shop',
      headline: "KD Barbershop opened its doors in {foundedYear}. The mission hasn't changed since.",
      subline: 'Great cuts. No attitude. Everyone welcome.',
    },
    story: {
      headline: 'How KD started',
      paragraphs: [
        "Kenny Diaz has been cutting hair for {kennyYearsExperience} years. He'd worked in enough shops to know what he liked and what he didn't — and what he wanted his own shop to feel like when the door swung open.",
        'In {foundedYear}, he opened KD Barbershop on {streetName}. The plan was simple: hire barbers who take the craft seriously, keep prices fair, and make sure every person who walked in — whether it was their first cut here or their fiftieth — got treated like they belonged.',
        '{yearsSinceFounding} years later, the team is {barberCount} deep. Between them, they’ve done thousands of cuts and earned {combinedReviewTotal} reviews between Booksy and Google. Not because they chase reviews. Because the work speaks for itself.',
      ],
    },
    values: {
      headline: "Three things we don't compromise on.",
      craft: {
        label: 'Craft',
        body:
          'Every barber here has real experience. Not "started last month" experience — years-of-behind-the-chair experience. When you sit down, you’re in expert hands.',
      },
      respect: {
        label: 'Respect',
        body:
          'This shop is for everyone. Kids on their first cut. Grandpas coming in for a senior trim. Guys getting ready for their wedding. Nobody gets treated better or worse based on what they’re paying.',
      },
      community: {
        label: 'Community',
        body:
          'KD is a {city} shop. Kenny lives here, most of the team lives here, and we know our regulars by name. That’s the point.',
      },
    },
    team: { headline: 'The team' },
    comeBy: {
      body: "We're at {shopAddress} in {city}. Come in, take a seat, and see for yourself.",
      ctaBook: 'Book online',
      ctaDirections: 'Get directions',
    },
  },

  services: {
    hero: {
      kicker: 'The full menu',
      headline: 'Every cut. Every add-on. Every price.',
      subline: 'No hidden fees, no surprises. What you see is what you pay.',
    },
    haircuts: { headline: 'Haircuts' },
    addOns: {
      headline: 'Add-ons',
      intro:
        'Layer these onto any haircut. Just let your barber know when you sit down, or add them at booking.',
    },
    bookThis: 'Book this',
    cta: {
      headline: 'Ready to book?',
      body:
        'Pick your barber, pick your service, and grab your time. Or come by — we take walk-ins whenever there’s an open chair.',
      ctaBook: 'Book online',
      ctaWalkIn: 'Come in as walk-in',
    },
    descriptions: {
      'mens-fade': {
        description:
          'Our most-booked service. Tapered fade, cleaned up, sent home sharp. Add a beard line-up if you want the whole look tight.',
      },
      'fade-beard': {
        description:
          'Fade plus beard trim and shape-up. This is the "look put-together" package. Popular for guys with regular Friday appointments.',
      },
      'fade-beard-eyebrows': {
        description:
          'Full facial cleanup: fade, beard shape, and eyebrows tidied. Everything above the shoulders, done right.',
      },
      'boys-under-12': {
        description:
          "Kids' cuts, done with patience. First-time haircut? We've done thousands. They'll leave smiling.",
      },
      'senior-cut': {
        description: 'Discounted rate for our senior regulars. Bring ID the first time.',
      },
      // Card added post-v1 during integration — pending Kenny approval.
      'senior-cut-beard': {
        description: 'Same senior rate, with the beard trimmed and shaped. Bring ID the first time.',
      },
      'the-works': {
        description:
          'The signature. Haircut + beard shape + eyebrows + nose wax + hot towel. One hour in the chair, walking out feeling like a million bucks.',
      },
      'full-shape-beard': {
        description: 'Complete beard shape plus a full line-up. Great add-on to any haircut.',
      },
      'hot-towel': {
        description:
          'Classic hot towel treatment. Softens the beard, cleans up the skin, feels amazing. Add it to any beard service.',
      },
      designs: {
        description:
          'Freestyle designs, letters, or custom line work. Price varies with complexity — talk to your barber.',
      },
      'shape-up': {
        description: 'Between cuts? Come in for just a line-up. Fresh edges in 10 minutes.',
      },
      eyebrows: { description: 'Clean up the brows while you’re in the chair.' },
      'nose-wax': {
        description: 'Quick, painless, and honestly, more people need it than admit it.',
      },
    },
  },

  wizard: {
    panel: {
      header: 'Behind the counter',
      liveLabel: 'Live',
      steps: [
        { title: 'Service selected', meta: '{serviceName} · {price} · {duration} min' },
        { title: "Barber's book checked", meta: '{barberName} · availability synced' },
        { title: 'Chair held for 10 minutes', meta: 'Held until {holdExpiry}' },
        { title: 'Contact saved and tagged', meta: 'source: website · tag: web-booking' },
        { title: 'Confirmation text sent', meta: 'delivered in about 4 seconds, with the address' },
        { title: 'Reminders armed', meta: '{reminderPrimaryHours} hours out, then {reminderSecondaryHours} hours out' },
        { title: 'Review request scheduled', meta: '3 hours after you leave the chair' },
        { title: 'Rebook nudge queued', meta: "day 26 · «¿Listo para un recorte?»" },
      ],
      anyBarberMeta: 'Any barber · scanning all',
      footer: 'Everything above runs itself — nobody at the shop lifts a finger',
    },
    step1: {
      headline: 'What are we doing today?',
      subline: 'Pick your main service. You can add extras in a minute.',
    },
    step2: {
      headline: "Who's cutting?",
      subline: 'Or let us match you with the next open chair.',
      emptyState:
        "{barberFirstName}'s fully booked for the next 3 days. Want to see availability for another barber, or pick a later date?",
      noPreference: 'No preference — next available',
      emptyStateHeadline: "Nobody's on the clock right now.",
      emptyStateBody: "Kenny's team also books through Booksy — pick a time that works for you.",
      emptyStateBooksyCta: 'Book on Booksy',
    },
    step3: {
      headline: 'When works for you?',
      subline: "Times shown are your barber's actual openings.",
      emptyState: 'No openings this day. Try tomorrow, or pick a different barber.',
    },
    noAvailability: {
      fallbackIntro:
        "Nothing open on the day you picked? Kenny's team also books through Booksy — there may be slots there we don't show here.",
      booksyCta: 'Book on Booksy',
    },
    step4: {
      headline: 'Anything else while you’re here?',
      subline: "Optional. Skip if you're just here for the main event.",
      runningTotal: 'Your appointment: {service} + {addOns} · {totalTime} · {totalPrice}',
      ctaContinue: 'Continue →',
      ctaSkip: 'Skip add-ons',
    },
    step5: {
      headline: 'Almost done.',
      subline: "We'll text you a confirmation and a reminder before your appointment.",
      fields: {
        firstName: 'First name',
        lastName: 'Last name',
        phone: 'Phone',
        email: 'Email (optional)',
      },
      consentTransactional: {
        label: 'Text me appointment confirmations and reminders.',
        body:
          'By checking this box, you agree to receive appointment-related SMS from KD Barbershop. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help.',
      },
      consentMarketing: {
        label: 'Also send me occasional promotions and specials.',
        body: 'Optional. Same STOP/HELP rules apply.',
      },
      ctaConfirm: 'Confirm booking',
    },
    confirmation: {
      headline: "You're booked. 👑",
      withBarber: 'with {barberFirstName}',
      body:
        "We'll text you a confirmation right now, a reminder {reminderPrimaryHours} hours before, and another {reminderSecondaryHours} hours before. If anything comes up, just reply to any text or call {shopPhone}.",
      ctaCalendar: 'Add to calendar',
      ctaDirections: 'Get directions',
    },
    errors: {
      slotTaken:
        'That slot was just booked by someone else. Take another 30 seconds and pick a different time?',
      phoneLookupFailed:
        "Hmm — we can't find that phone number. That's fine, we'll create a new profile for you when you finish booking.",
      generic:
        "Something didn't quite work. Give it another try, or call us at {shopPhone} and we'll book you in directly.",
    },
  },

  walkInStatus: {
    upWith: "You're up with {barber}.",
    upSubline: 'Head to the chair.',
    inLineWith: "You're in line with {barber}.",
    inLineSubline: 'About {minutes} min.',
    positionNext: "You're next in line.",
    positionNth: "You're #{position} in line.",
    nextBarber: 'the next barber',
    willCall: "We'll call your name.",
  },

  kioskWalkIn: {
    reset: {
      countdown: 'Back to start in {seconds}',
      done: 'Done',
      idleHeadline: 'Still there?',
      idleBody: 'Starting over in {seconds} so the next person gets a clean screen.',
      idleStay: "Yes, I'm here",
    },
    service: {
      headline: 'What are we doing today?',
      subline: 'Pick your main service. You can add extras in a minute.',
    },
    addOns: {
      headline: 'Anything else while you’re here?',
      subline: "Optional. Skip if you're just here for the main event.",
      runningTotal: '{service} + {addOns} · {totalTime} · {totalPrice}',
      ctaContinue: 'Continue →',
      ctaSkip: 'Skip add-ons',
    },
    barber: {
      headline: 'Who do you want cutting today?',
      nextAvailable: 'Next available',
      onTheClock: '{count} on the clock',
      freeNow: 'Free now',
      withClient: 'With a client — you can wait',
      emptyHeadline: 'No barbers on the clock right now.',
      emptySubline: 'We open at {nextPostedOpen}.',
    },
    contact: {
      headline: 'Last thing.',
      subline: 'So we can find you when a chair opens up.',
      nameLabel: 'Your name',
      phoneLabel: 'Phone',
      ctaSubmit: 'Check in',
    },
    result: {
      assignedHeadline: "You're up with {barberFirstName}.",
      assignedSubline: 'Approximately {duration} min.',
      busyHeadline: 'All barbers are busy right now.',
      busyPreferred: '{barberFirstName} is with a client.',
      positionNext: "You're next in line.",
      positionNth: "You're #{position} in line.",
      ctaWait: "I'll wait",
      ctaLeave: 'Come back later',
      leftMessage: 'Come back anytime.',
    },
    errors: {
      shopEmpty: 'No barbers on the clock. Try again later.',
      duplicate: "You're already checked in. Show this screen to your barber: {shortId}",
      generic: "Check-in didn't go through. Try again or ask the front desk.",
    },
    back: '← Back',
    waiting: {
      loading: 'Checking the line…',
      readyHeadline: "You're up with {barberFirstName}!",
      readySubline: 'Head back to the shop.',
      inLineKicker: "You're in line",
      positionNext: "You're next",
      positionNth: '#{position}',
      waitingForBarber: 'Waiting for {barberFirstName}',
      willCall: "We'll call your name when a chair opens up.",
      projected: 'Next available: {projectedBarber} in about {projectedWaitMinutes} min.',
      doneHeadline: 'Your cut is complete.',
      doneSubline: 'Thanks for coming in.',
      releasedHeadline: 'Your spot has been released.',
      releasedSubline: 'Come back anytime.',
      notFoundHeadline: "We can't find that spot in the line.",
      notFoundSubline: 'It may have expired. Come see us at the front desk, or call {shopPhone}.',
      scanHint: 'Scan to watch from your phone',
      cancelCta: 'Cancel my spot',
    },
  },

  mobile: {
    stickyBar: { book: 'Book', walkin: 'Walk-in', call: 'Call', label: 'Quick actions' },
  },

  footer: {
    directionsCta: 'Get directions',
    followLabel: 'Follow',
    exploreLabel: 'Explore',
    booksyCta: 'Book on Booksy',
    signoff: 'See you soon. © {currentYear} {shopName} · {city} {state}',
    legal: {
      copyright: '© {currentYear} {shopName}',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      smsTerms: 'SMS Terms',
      builtBy: 'Site by SmartCity Contractors',
    },
  },
} satisfies CopyDict;

export default en;
